import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * US Zip Code validator.
 * Accepts 5-digit or 9-digit (ZIP+4) formats.
 *
 * @returns Angular ValidatorFn
 *
 * @example
 * zip: ['', [Validators.required, zipcodeValidator()]]
 */
export function zipcodeValidator(): ValidatorFn {
  return usZipcodeValidator();
}

/**
 * US Zip Code validator.
 * Accepts: 12345, 12345-6789, 12345 6789
 *
 * @returns Angular ValidatorFn
 */
export function usZipcodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Allow null/undefined/empty - let Validators.required handle required validation
    if (!value) {
      return null;
    }

    // Trim whitespace
    const trimmedValue = typeof value === 'string' ? value.trim() : String(value);

    if (trimmedValue === '') {
      return null;
    }

    // US Zip Code pattern: 5 digits or 5 digits + dash/space + 4 digits
    const zipPattern = /^\d{5}(?:[-\s]\d{4})?$/;

    if (!zipPattern.test(trimmedValue)) {
      return {
        zipcode: {
          message: 'This is not a valid U.S. Zip Code. Must be either 5 or 9 digits in the format 99999 or 99999-9999.'
        }
      };
    }

    return null;
  };
}

/**
 * Canadian Postal Code validator.
 * Accepts format: A1A 1A1 (letter-digit-letter space digit-letter-digit)
 *
 * @returns Angular ValidatorFn
 *
 * @example
 * postalCode: ['', [canadianPostalCodeValidator()]]
 */
export function canadianPostalCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Allow null/undefined/empty
    if (!value) {
      return null;
    }

    // Trim whitespace
    const trimmedValue = typeof value === 'string' ? value.trim() : String(value);

    if (trimmedValue === '') {
      return null;
    }

    // Canadian postal code pattern: A1A 1A1
    const postalPattern = /^[a-zA-Z][0-9][a-zA-Z][\s][0-9][a-zA-Z][0-9]$/;

    if (!postalPattern.test(trimmedValue)) {
      return {
        postalCode: {
          message: 'Zip/Postal code must be in the following format A1A 1A1'
        }
      };
    }

    return null;
  };
}
